import {
  Button,
  DialogActionTrigger,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  HStack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';

import { CenteredDialogContent } from '#components/ui/centered-dialog-content';
import { toaster } from '#components/ui/toaster';

import { normalizeLinkUrl } from '../utils/apply-editor-link';

export type PasteEditorLinkHandlers = {
  applyLink: (href: string, label?: string) => void;
  cancel: () => void;
};

export interface PasteEditorLinkModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (href: string, label?: string) => void;
}

export function PasteEditorLinkModal({
  open,
  onOpenChange,
  onSubmit,
}: PasteEditorLinkModalProps) {
  const urlRef = useRef<HTMLInputElement>(null);
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');

  useEffect(() => {
    if (!open) return;

    setUrl('');
    setLabel('');
    queueMicrotask(() => urlRef.current?.focus());
  }, [open]);

  const handleSubmit = () => {
    const normalized = normalizeLinkUrl(url);
    if (!normalized) {
      toaster.create({
        title: 'Invalid URL',
        description: 'Enter a valid URL, path, or hash.',
        type: 'warning',
      });
      return;
    }

    const trimmed = label.trim();
    onSubmit(normalized, trimmed.length > 0 ? trimmed : undefined);
  };

  return (
    <DialogRoot
      open={open}
      onOpenChange={(e) => {
        if (!e.open) onOpenChange(false);
      }}
    >
      <CenteredDialogContent maxW="min(90vw, 28rem)">
        <DialogHeader>
          <DialogTitle>Add link</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <VStack gap={3} align="stretch">
            <Text fontSize="sm" color="fg.muted">
              Paste or type a URL. You can optionally set different visible
              text.
            </Text>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                URL
              </Text>
              <input
                ref={urlRef}
                type="text"
                inputMode="url"
                placeholder="https://…"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSubmit();
                }}
                autoComplete="off"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: '6px',
                  border: '1px solid var(--chakra-colors-border)',
                  background: 'var(--chakra-colors-bg)',
                  color: 'var(--chakra-colors-fg)',
                }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                Visible text (optional)
              </Text>
              <input
                type="text"
                placeholder="Leave blank to use selection or URL"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSubmit();
                }}
                autoComplete="off"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  borderRadius: '6px',
                  border: '1px solid var(--chakra-colors-border)',
                  background: 'var(--chakra-colors-bg)',
                  color: 'var(--chakra-colors-fg)',
                }}
              />
            </label>
          </VStack>
        </DialogBody>
        <DialogFooter>
          <HStack gap={2} flexWrap="wrap">
            <DialogActionTrigger asChild>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
            </DialogActionTrigger>
            <Button colorPalette="blue" onClick={handleSubmit}>
              Insert link
            </Button>
          </HStack>
        </DialogFooter>
      </CenteredDialogContent>
    </DialogRoot>
  );
}
